import React, { useRef, useCallback, useEffect, useState, memo } from 'react';

interface SearchInputProps {
  initialValue: string;
  onQueryChange: (query: string) => void;
  onFocusChange: (focused: boolean) => void;
  focused: boolean;
  debounceMs?: number;
}

export const SearchInput = memo(function SearchInput({
  initialValue,
  onQueryChange,
  onFocusChange,
  focused,
  debounceMs = 200,
}: SearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hasValue, setHasValue] = useState(!!initialValue);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setHasValue(!!val.trim());

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!val.trim()) {
      onQueryChange('');
    } else {
      debounceRef.current = setTimeout(() => {
        onQueryChange(val);
      }, debounceMs);
    }
  }, [onQueryChange, debounceMs]);

  const handleClear = useCallback(() => {
    if (inputRef.current) inputRef.current.value = '';
    setHasValue(false);
    onQueryChange('');
    inputRef.current?.focus();
  }, [onQueryChange]);

  const handleCancel = useCallback(() => {
    if (inputRef.current) inputRef.current.value = '';
    setHasValue(false);
    onQueryChange('');
    onFocusChange(false);
    inputRef.current?.blur();
  }, [onQueryChange, onFocusChange]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <>
      <div className="flex-1 min-w-0 relative">
        <div className="bg-background-secondary rounded-[14px] px-3.5 py-2 flex items-center">
          <svg
            className="w-[16px] h-[16px] mr-2.5 flex-shrink-0"
            style={{ color: '#1A1D1A' }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="Поиск"
            defaultValue={initialValue}
            onChange={handleChange}
            onFocus={() => onFocusChange(true)}
            className="flex-1 outline-none text-sm bg-transparent min-w-0 placeholder:text-[#1A1D1A]"
            style={{ color: '#1A1D1A' }}
          />
          {hasValue && (
            <button
              onClick={handleClear}
              className="ml-1.5 p-0.5 text-text-tertiary hover:text-text-secondary transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {focused && (
        <button
          onClick={handleCancel}
          className="flex-shrink-0 text-primary text-sm font-medium whitespace-nowrap"
        >
          Отмена
        </button>
      )}
    </>
  );
});
